import { css, type CSSResult } from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';

/**
 * Markdown service for harness messages. Uses smartmarkdown (already in the
 * dependency graph via dees-domtools; GFM-capable, sanitized-by-default) so
 * the group adds zero dependencies. Results are LRU-cached because streaming
 * re-parses the same growing text repeatedly. Swap seam: if the parser ever
 * changes, only this file does.
 */
export class HarnessMarkdown {
  private static instance: HarnessMarkdown | undefined;

  public static getInstance(): HarnessMarkdown {
    HarnessMarkdown.instance ??= new HarnessMarkdown();
    return HarnessMarkdown.instance;
  }

  private cache = new Map<string, string>();
  private static readonly maxCacheEntries = 200;

  public async renderToHtml(markdownText: string): Promise<string> {
    const cached = this.cache.get(markdownText);
    if (cached !== undefined) {
      // refresh LRU position
      this.cache.delete(markdownText);
      this.cache.set(markdownText, cached);
      return cached;
    }
    const html = await domtools.plugins.smartmarkdown.SmartMarkdown.easyMarkdownToHtml(markdownText);
    const processed = this.postProcess(html);
    this.cache.set(markdownText, processed);
    if (this.cache.size > HarnessMarkdown.maxCacheEntries) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) this.cache.delete(oldestKey);
    }
    return processed;
  }

  /** External links open in a new context and never leak the opener. */
  private postProcess(html: string): string {
    return html.replaceAll('<a href=', '<a target="_blank" rel="noopener noreferrer" href=');
  }
}

/**
 * Token-based typography for rendered markdown. Included by components that
 * render HarnessMarkdown output inside a `.markdown-body` container. Code
 * fence syntax colors reuse the codebox palette convention (dark defaults,
 * `:host([gobright])` for light) — kept in the consuming component's styles
 * where :host is available; this block covers structure and rhythm.
 */
export const markdownStyles: CSSResult = css`
  .markdown-body {
    color: var(--dees-color-text-primary);
    font-size: var(--dees-font-control-size, 13px);
    line-height: 1.55;
    overflow-wrap: anywhere;
  }
  .markdown-body > :first-child {
    margin-top: 0;
  }
  .markdown-body > :last-child {
    margin-bottom: 0;
  }
  .markdown-body h1,
  .markdown-body h2,
  .markdown-body h3,
  .markdown-body h4 {
    margin: var(--dees-spacing-lg) 0 var(--dees-spacing-sm);
    color: var(--dees-color-text-primary);
    font-weight: 600;
    line-height: 1.3;
  }
  .markdown-body h1 { font-size: 1.35em; }
  .markdown-body h2 { font-size: 1.2em; }
  .markdown-body h3 { font-size: 1.05em; }
  .markdown-body h4 { font-size: 1em; }
  .markdown-body p {
    margin: var(--dees-spacing-sm) 0;
  }
  .markdown-body ul,
  .markdown-body ol {
    margin: var(--dees-spacing-sm) 0;
    padding-left: 1.4em;
  }
  .markdown-body li {
    margin: 2px 0;
  }
  .markdown-body a {
    color: var(--dees-color-link);
    text-decoration: none;
  }
  .markdown-body a:hover {
    color: var(--dees-color-link-hover);
    text-decoration: underline;
  }
  .markdown-body blockquote {
    margin: var(--dees-spacing-sm) 0;
    padding: var(--dees-spacing-xs) var(--dees-spacing-md);
    border-left: 3px solid var(--dees-color-border-strong);
    color: var(--dees-color-text-secondary);
  }
  .markdown-body code {
    padding: 1px 5px;
    border-radius: var(--dees-radius-xs);
    background: var(--dees-color-code-bg);
    font-family: var(--dees-font-family-mono);
    font-size: 0.92em;
  }
  .markdown-body pre {
    margin: var(--dees-spacing-sm) 0;
    padding: var(--dees-spacing-md);
    border: 1px solid var(--dees-color-border-subtle);
    border-radius: var(--dees-radius-md);
    corner-shape: var(--dees-corner-shape);
    background: var(--dees-color-code-block-bg);
    overflow: auto;
  }
  .markdown-body pre code {
    padding: 0;
    border-radius: 0;
    background: transparent;
    font-size: var(--dees-font-control-size-sm, 12px);
    line-height: 1.5;
  }
  .markdown-body table {
    margin: var(--dees-spacing-sm) 0;
    border-collapse: collapse;
    font-size: 0.95em;
  }
  .markdown-body th,
  .markdown-body td {
    padding: 4px 10px;
    border: 1px solid var(--dees-color-border-default);
    text-align: left;
  }
  .markdown-body th {
    background: var(--dees-color-bg-tertiary);
    font-weight: 600;
  }
  .markdown-body img {
    max-width: 100%;
    border-radius: var(--dees-radius-md);
  }
  .markdown-body hr {
    margin: var(--dees-spacing-md) 0;
    border: 0;
    border-top: 1px solid var(--dees-color-border-subtle);
  }
`;
